import type { IChatMessage } from '@kognitos/lattice';

import type {
  IRainToolCallState,
  IRainToolRenderContext,
} from '@/modules/rain/hooks/types';
import type { TRainValueJs } from '@/modules/rain/utils/value';

import { AgentArtifactFileCard } from './AgentArtifactFileCard';
import {
  getArtifactExtension,
  getArtifactFilename,
  type IAgentArtifactFile,
} from '../utils/artifact-file';

/** Reads a field off a decoded tool value only when it is a plain object. */
const readField = (
  value: TRainValueJs | undefined,
  field: string,
): TRainValueJs | undefined => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  return (value as Record<string, TRainValueJs>)[field];
};

const asString = (value: TRainValueJs | undefined): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

/**
 * Sizes arrive as protobuf int64s, which encode as JSON STRINGS ("6162") rather
 * than numbers. Both are accepted so a backend that switches encodings doesn't
 * silently drop the size from the card.
 */
const asByteCount = (value: TRainValueJs | undefined): number | undefined => {
  if (typeof value === 'number')
    return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Renders `artifact_write` / `artifact_edit` as a file card instead of the
 * generic tool-call card.
 *
 * The URI is read from the tool's ARGUMENTS rather than its result, so the card
 * can name the file while the write is still streaming; the result contributes
 * the committed size.
 *
 * A FAILED write gets no file card. A file chip is an offer to open something,
 * and a rejected write (`invalid path: expected {id}--{filename}`) produced no
 * file to open — dressing that up as a file, even a red one, gives a retry the
 * agent usually makes on its own the same visual weight as a real output. The
 * generic tool-call card already states the failure and its reason in the
 * runtime's own vocabulary, so failures fall through to it.
 */
export function renderArtifactFileCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  if (state.status === 'error') return ctx.renderGenericCard(state);

  const uri = asString(state.args.uri);

  // No URI yet: the ToolCallStart landed but its arguments haven't streamed in.
  // The generic card's "Writing…" status line covers this better than a file
  // card with no filename would.
  if (uri === undefined) return ctx.renderGenericCard(state);

  const filename = getArtifactFilename(uri);
  const resultSize = asByteCount(readField(state.result, 'size'));

  const file: IAgentArtifactFile = {
    uri,
    filename,
    extension: getArtifactExtension(filename),
    sizeBytes: resultSize,
    description: asString(state.args.description),
  };

  return ctx.renderCustomCard(
    state,
    <AgentArtifactFileCard
      file={file}
      status={state.status === 'running' ? 'writing' : 'ready'}
    />,
  );
}
